import { Audio } from '@rhg/components/media/audio-player/audio';
import { MediaEl, PlayerAudio } from '@rhg/components/media/types';
import { useMediaControls } from '@rhg/hooks/use-media-controls';
import {
    createContext,
    ReactNode,
    useCallback,
    useMemo,
    useState,
} from 'react';
import { AudioState, LoadAudioProps } from './types';

export const AudioContext = createContext<AudioState>(null!);

interface Props {
    children: ReactNode;
}
export const AudioProvider = ({ children }: Props) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [autoplay, setAutoplay] = useState(false);
    const [hasAutoPlayRestriction, setHasAutoPlayRestriction] = useState(false);
    const [loop, setLoop] = useState(false);
    const [audio, setAudio] = useState<PlayerAudio | null>(null);
    const [mediaEl, setMediaEl] = useState<MediaEl>();
    const [onClose, setOnClose] = useState<(() => void) | undefined>(undefined);

    const controls = useMediaControls({
        type: 'audio',
        mediaEl: mediaEl ?? null,
        isInitiallyMuted: false,
    });

    const { toggleMuted } = controls;

    const credentials = useMemo(
        () => (audio?.needsCredentials ? `?${audio?.credentials}` : ''),
        [audio?.credentials, audio?.needsCredentials]
    );

    const sources = useMemo(
        () =>
            audio?.src && audio?.type
                ? [
                      {
                          src: `${audio.src}${credentials}`,
                          type: audio?.type,
                      },
                  ]
                : undefined,
        [audio?.src, audio?.type, credentials]
    );

    const subtitleSources = useMemo(
        () =>
            audio?.subtitleSrc && audio?.subtitleType
                ? [
                      {
                          src: `${audio.subtitleSrc}${credentials}`,
                          type: audio.subtitleType,
                      },
                  ]
                : undefined,
        [audio?.subtitleSrc, audio?.subtitleType, credentials]
    );

    const options = useMemo(
        () => ({
            autoplay,
            loop,
            controls: false,
        }),
        [autoplay, loop]
    );

    const loadAudio = useCallback(
        async ({
            audio: audio_,
            autoplay: autoplay_,
            loop: loop_,
            onClose: onClose_,
            isInitiallyMuted,
        }: LoadAudioProps) => {
            setOnClose(() => onClose_);
            setAutoplay(!!autoplay_);
            setLoop(!!loop_);
            toggleMuted(isInitiallyMuted);
            setAudio(audio_);
            setIsLoaded(true);
        },
        [toggleMuted]
    );

    const unloadAudio = useCallback(() => {
        setIsLoaded(false);
        setMediaEl((mediaEl) => {
            if (mediaEl) {
                mediaEl.currentTime = 0;
            }
            return undefined;
        });
        setAudio(null);
        setAutoplay(false);
        setLoop(false);
        toggleMuted(false);
        setOnClose(undefined);
    }, [toggleMuted]);

    return (
        <AudioContext.Provider
            value={{
                loadAudio,
                unloadAudio,
                mediaEl,
                audio,
                controls,
                isLoaded: isLoaded && !!mediaEl,
                hasAutoPlayRestriction,
            }}
        >
            {children}
            {isLoaded && (
                <Audio
                    sources={sources}
                    subtitleSources={subtitleSources}
                    options={options}
                    setMediaEl={setMediaEl}
                    needsCredentials={audio?.needsCredentials}
                    onClose={onClose}
                    setHasAutoPlayRestriction={setHasAutoPlayRestriction}
                />
            )}
        </AudioContext.Provider>
    );
};
